import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { ProcessDefinition, WorkflowApiService } from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-definitions',
  imports: [
    AinPageComponent,
    AinStateComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    FormsModule,
    RouterLink
  ],
  providers: [MessageService],
  template: `
    <ain-page [title]="t('workflow.definitions.title')" [description]="t('workflow.definitions.description')">
      <div class="toolbar">
        <button pButton type="button" icon="pi pi-plus" [label]="t('common.create')" (click)="showCreate.set(true)"></button>
      </div>
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else {
        <p-table [value]="definitions()" [paginator]="true" [rows]="10">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('columns.name') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('columns.version') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.name }}</td>
              <td>{{ row.status }}</td>
              <td>{{ row.currentVersion }}</td>
              <td><a [routerLink]="['/workflow/definitions', row.id]">{{ t('common.open') }}</a></td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
    <p-dialog [header]="t('workflow.definitions.create')" [(visible)]="showCreateVisible" [modal]="true" [style]="{ width: '28rem' }">
      <div class="form-field">
        <label>{{ t('columns.name') }}</label>
        <input pInputText [(ngModel)]="newName" class="w-full" />
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="showCreate.set(false)"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="create()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { margin-bottom: 16px; }
    .form-field { display: grid; gap: 8px; margin-bottom: 12px; }
    .w-full { width: 100%; }
    a { color: var(--ain-color-primary); font-weight: 600; }
  `]
})
export class WorkflowDefinitionsComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly router = inject(Router);

  readonly definitions = signal<ProcessDefinition[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly showCreate = signal(false);
  newName = '';

  get showCreateVisible(): boolean {
    return this.showCreate();
  }
  set showCreateVisible(value: boolean) {
    this.showCreate.set(value);
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.api.listDefinitions().subscribe({
      next: items => {
        this.definitions.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  create(): void {
    if (!this.newName.trim()) return;
    this.api.createDefinition({ name: this.newName.trim() }).subscribe({
      next: item => {
        this.showCreate.set(false);
        this.newName = '';
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
        void this.router.navigate(['/workflow/definitions', item.id]);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
